/**
 * Wiki page `aria-live="polite"` helpers.
 *
 * Two surfaces:
 *
 *   - `announceToWiki(text)` — plain function callable from any
 *     context (TipTap slash-command runs, ProseMirror plugins,
 *     standalone helpers). Writes into the SR-only live region
 *     mounted at `<div id="wiki-announce" />` inside DocPage.
 *
 *   - `useWikiAnnounce()` — React hook returning a stable callback
 *     bound to the same helper. Components inside the wiki surface
 *     use this so the announce identity stays stable across renders
 *     and slot cleanly into effect deps.
 *
 * Why a separate helper? Slash-command `run()`s live in
 * `lib/slash-commands.ts` — pure functions, no React context. They
 * still need to fire announcements ("Information panel inserted"
 * etc.) so screen-reader users get parity feedback with the visible
 * insertion. Centralizing the DOM target name (`#wiki-announce`)
 * keeps the coupling between the page mount + every caller in one
 * place.
 *
 * RAF-clear-then-set: the live region only re-announces on text-node
 * mutation, so identical strings written back-to-back are silent.
 * Clearing first, then setting on the next frame, guarantees the
 * announcement fires even when the same string repeats.
 */

import { useCallback } from 'react'

const TARGET_ID = 'wiki-announce'

/** Write `text` into the wiki page's `aria-live="polite"` region. */
export function announceToWiki(text: string): void {
  if (typeof document === 'undefined') return
  const target = document.getElementById(TARGET_ID)
  if (!target) return
  target.textContent = ''
  requestAnimationFrame(() => {
    target.textContent = text
  })
}

export function useWikiAnnounce() {
  const announce = useCallback((text: string) => {
    announceToWiki(text)
  }, [])
  return announce
}
